import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";

function renderMarkdownLite(content) {
  // very light markdown — headings, bold, tables
  const lines = content.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("## ")) {
      out.push(<h2 key={i} className="mt-10 text-2xl font-display font-bold text-[#0A0D14]">{line.slice(3)}</h2>);
      i++; continue;
    }
    if (line.startsWith("|") && lines[i+1]?.includes("---")) {
      const headers = line.split("|").map(s => s.trim()).filter(Boolean);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(lines[i].split("|").map(s => s.trim()).filter(Boolean));
        i++;
      }
      out.push(
        <div key={`t-${i}`} className="mt-6 border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] text-left">
              <tr>{headers.map((h, j) => <th key={j} className="px-3 py-2 font-medium text-[#0A0D14]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, k) => (
                <tr key={k} className="border-t border-[#E2E8F0]">
                  {r.map((c, l) => <td key={l} className="px-3 py-2 text-[#525B6A] tabular-nums">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (line.trim() === "") { i++; continue; }
    // bold parsing
    const parts = line.split(/\*\*(.+?)\*\*/g);
    out.push(<p key={i} className="mt-4 text-[17px] leading-relaxed text-[#525B6A]">
      {parts.map((p, j) => j % 2 === 1 ? <strong key={j} className="text-[#0A0D14]">{p}</strong> : p)}
    </p>);
    i++;
  }
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/blog/${slug}`).then((r) => setData(r.data)).catch(() => setData({ notFound: true }));
  }, [slug]);

  const toc = useMemo(() => {
    if (!data?.post) return [];
    return data.post.content.split("\n").filter(l => l.startsWith("## ")).map(l => l.slice(3));
  }, [data]);

  if (!data) return <div className="max-w-3xl mx-auto px-6 py-24 text-[#8A94A6]">Loading…</div>;
  if (data.notFound) return <div className="max-w-3xl mx-auto px-6 py-24 text-[#8A94A6]">Post not found. <Link to="/blog" className="text-[#0047FF]">Back to blog</Link></div>;

  const p = data.post;
  const canonical = p.canonical_url || `/blog/${p.slug}`;

  return (
    <>
      <SEO title={p.meta_title || `${p.title} — SerpNex`} description={p.meta_description || p.excerpt} canonical={canonical} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <Link to="/blog" className="inline-flex items-center gap-1 text-xs text-[#525B6A] hover:text-[#0047FF]"><ArrowLeft className="h-3 w-3" /> Back to blog</Link>
        <div className="mt-6 eyebrow">{p.category}</div>
        <h1 className="mt-3 h-display text-4xl sm:text-5xl font-bold text-[#0A0D14] leading-tight">{p.title}</h1>
        <div className="mt-4 text-sm text-[#8A94A6]">{new Date(p.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} · {p.author}</div>

        {toc.length > 0 && (
          <div className="mt-8 card-crisp p-5">
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium mb-2">In this post</div>
            <ul className="space-y-1 text-sm">
              {toc.map((t, i) => <li key={i} className="text-[#525B6A]">· {t}</li>)}
            </ul>
          </div>
        )}

        <div className="mt-6">{renderMarkdownLite(p.content)}</div>

        <div className="mt-14 border-t border-[#E2E8F0] pt-8">
          <div className="card-crisp p-6 bg-[#F8F9FA]">
            <div className="text-xs uppercase tracking-wider text-[#0047FF] font-medium">Try SerpNex</div>
            <div className="mt-2 text-xl font-display font-semibold text-[#0A0D14]">Turn this reading into action.</div>
            <div className="mt-2 text-sm text-[#525B6A]">Track your keywords daily — with a clear morning executive summary and configurable alert rules.</div>
            <Link to="/signup" className="mt-4 inline-flex btn-primary px-4 py-2 rounded-md text-sm items-center gap-1">Start 7-Day Free Trial <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </div>

        {data.related?.length > 0 && (
          <div className="mt-14">
            <div className="eyebrow">Related</div>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {data.related.map((r) => (
                <Link to={`/blog/${r.slug}`} key={r.slug} className="card-crisp p-4 hover:border-[#0047FF] transition-colors">
                  <div className="text-xs text-[#0047FF]">{r.category}</div>
                  <div className="mt-1 font-display font-semibold text-[#0A0D14]">{r.title}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
}
